/**
 * Sample-Value Profiling Stream Hook
 *
 * Streams live status for a catalog table's sample-value profiling job via SSE.
 * The backend derives progress from the Celery task state plus each column's
 * persisted `sample_values_status`, so this hook just surfaces "is it running"
 * and the terminal result.
 */

import { useEffect, useRef, useState } from 'react';
import { AXIOS_INSTANCE } from '../services/api-client';

export type ProfileSamplesEventType =
  | 'started'
  | 'progress'
  | 'heartbeat'
  | 'completed'
  | 'failed'
  | 'timeout';

export interface ProfileSamplesSummary {
  total: number;
  indexed: number;
  stale: number;
  disabled: number;
  profiling_failed: number;
  not_profiled: number;
  [key: string]: number;
}

export interface ProfileSamplesEvent {
  event_type: ProfileSamplesEventType;
  table_id?: number;
  config_id?: number;
  task_id?: string;
  task_state?: string | null;
  summary?: ProfileSamplesSummary;
  result?: Record<string, unknown>;
  error?: string;
  timestamp: string;
}

export interface ProfileSamplesStreamState {
  isConnected: boolean;
  isRunning: boolean;
  latest?: ProfileSamplesEvent;
  summary?: ProfileSamplesSummary;
  completion?: ProfileSamplesEvent;
  failure?: ProfileSamplesEvent;
}

interface UseProfileSamplesStreamArgs {
  tableId: number | null;
  taskId: string | null;
  onComplete?: (event: ProfileSamplesEvent) => void;
  onError?: (event: ProfileSamplesEvent) => void;
}

const TERMINAL_EVENTS: ProfileSamplesEventType[] = ['completed', 'failed', 'timeout'];

export function useProfileSamplesStream({
  tableId,
  taskId,
  onComplete,
  onError,
}: UseProfileSamplesStreamArgs): ProfileSamplesStreamState {
  const [state, setState] = useState<ProfileSamplesStreamState>({
    isConnected: false,
    isRunning: false,
  });
  const eventSourceRef = useRef<EventSource | null>(null);
  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);
  onCompleteRef.current = onComplete;
  onErrorRef.current = onError;

  useEffect(() => {
    if (!tableId || !taskId) {
      eventSourceRef.current?.close();
      eventSourceRef.current = null;
      setState({ isConnected: false, isRunning: false });
      return;
    }

    const apiUrl = (AXIOS_INSTANCE.defaults.baseURL || process.env.REACT_APP_API_URL || '').replace(
      /\/$/,
      ''
    );
    const token = localStorage.getItem('auth_token');
    if (!token) {
      console.error('No auth token found for profiling SSE connection');
      return;
    }

    setState({ isConnected: false, isRunning: true });

    const url =
      `${apiUrl}/api/v1/registry/tables/${tableId}/profile-samples/stream` +
      `?token=${encodeURIComponent(token)}&task_id=${encodeURIComponent(taskId)}`;
    const eventSource = new EventSource(url, { withCredentials: false });
    eventSourceRef.current = eventSource;

    eventSource.onopen = () => {
      setState((prev) => ({ ...prev, isConnected: true }));
    };

    eventSource.onmessage = (event) => {
      let parsed: ProfileSamplesEvent;
      try {
        parsed = JSON.parse(event.data);
      } catch (error) {
        console.error('Failed to parse profiling SSE event:', error);
        return;
      }

      const isTerminal = TERMINAL_EVENTS.includes(parsed.event_type);
      setState((prev) => ({
        isConnected: !isTerminal && prev.isConnected,
        isRunning: !isTerminal,
        latest: parsed,
        summary: parsed.summary ?? prev.summary,
        completion: parsed.event_type === 'completed' ? parsed : prev.completion,
        failure:
          parsed.event_type === 'failed' || parsed.event_type === 'timeout'
            ? parsed
            : prev.failure,
      }));

      if (isTerminal) {
        eventSource.close();
        eventSourceRef.current = null;
        if (parsed.event_type === 'completed') {
          onCompleteRef.current?.(parsed);
        } else {
          onErrorRef.current?.(parsed);
        }
      }
    };

    eventSource.onerror = (error) => {
      console.error('Profiling SSE connection error:', error);
      eventSource.close();
      eventSourceRef.current = null;
      setState((prev) => ({ ...prev, isConnected: false, isRunning: false }));
    };

    return () => {
      eventSourceRef.current?.close();
      eventSourceRef.current = null;
    };
  }, [tableId, taskId]);

  return state;
}

interface UseProfileConnectorSamplesStreamArgs {
  connectorId: number | null;
  taskId: string | null;
  onComplete?: (event: ProfileSamplesEvent) => void;
  onError?: (event: ProfileSamplesEvent) => void;
}

export function useProfileConnectorSamplesStream({
  connectorId,
  taskId,
  onComplete,
  onError,
}: UseProfileConnectorSamplesStreamArgs): ProfileSamplesStreamState {
  const [state, setState] = useState<ProfileSamplesStreamState>({
    isConnected: false,
    isRunning: false,
  });
  const eventSourceRef = useRef<EventSource | null>(null);
  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);
  onCompleteRef.current = onComplete;
  onErrorRef.current = onError;

  useEffect(() => {
    if (!connectorId || !taskId) {
      eventSourceRef.current?.close();
      eventSourceRef.current = null;
      setState({ isConnected: false, isRunning: false });
      return;
    }

    const apiUrl = (AXIOS_INSTANCE.defaults.baseURL || process.env.REACT_APP_API_URL || '').replace(
      /\/$/,
      ''
    );
    const token = localStorage.getItem('auth_token');
    if (!token) {
      console.error('No auth token found for profiling SSE connection');
      return;
    }

    setState({ isConnected: false, isRunning: true });

    const url =
      `${apiUrl}/api/v1/registry/connectors/${connectorId}/profile-samples/stream` +
      `?token=${encodeURIComponent(token)}&task_id=${encodeURIComponent(taskId)}`;
    const eventSource = new EventSource(url, { withCredentials: false });
    eventSourceRef.current = eventSource;

    eventSource.onopen = () => {
      setState((prev) => ({ ...prev, isConnected: true }));
    };

    eventSource.onmessage = (event) => {
      let parsed: ProfileSamplesEvent;
      try {
        parsed = JSON.parse(event.data);
      } catch (error) {
        console.error('Failed to parse profiling SSE event:', error);
        return;
      }

      const isTerminal = TERMINAL_EVENTS.includes(parsed.event_type);
      setState((prev) => ({
        isConnected: !isTerminal && prev.isConnected,
        isRunning: !isTerminal,
        latest: parsed,
        summary: prev.summary,
        completion: parsed.event_type === 'completed' ? parsed : prev.completion,
        failure:
          parsed.event_type === 'failed' || parsed.event_type === 'timeout'
            ? parsed
            : prev.failure,
      }));

      if (isTerminal) {
        eventSource.close();
        eventSourceRef.current = null;
        if (parsed.event_type === 'completed') {
          onCompleteRef.current?.(parsed);
        } else {
          onErrorRef.current?.(parsed);
        }
      }
    };

    eventSource.onerror = (error) => {
      console.error('Profiling SSE connection error:', error);
      eventSource.close();
      eventSourceRef.current = null;
      setState((prev) => ({ ...prev, isConnected: false, isRunning: false }));
    };

    return () => {
      eventSourceRef.current?.close();
      eventSourceRef.current = null;
    };
  }, [connectorId, taskId]);

  return state;
}
